import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const blank = {
  code: "", name: "", description: "", price_monthly: 0, price_yearly: 0, trial_days: 0,
  module_keys: [], available_addons: [], max_users: "", max_invoices_per_month: "",
  max_parties: "", max_items: "",
  is_active: true, is_default: false, sort_order: 100,
};

// "" / null in a limit field means unlimited.
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const limitLabel = (v) => (v === null || v === undefined ? "Unlimited" : v);

export default function Plans() {
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/platform/plans").then(({ data }) => setRows(data));

  useEffect(() => {
    load();
    api.get("/platform/modules").then(({ data }) => setModules(data));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...blank, ...p,
      available_addons: p.available_addons || [],
      max_users: p.max_users ?? "",
      max_invoices_per_month: p.max_invoices_per_month ?? "",
      max_parties: p.max_parties ?? "",
      max_items: p.max_items ?? "",
    });
    setOpen(true);
  };

  const toggleModule = (key) =>
    setForm((f) => ({
      ...f,
      module_keys: f.module_keys.includes(key)
        ? f.module_keys.filter((k) => k !== key)
        : [...f.module_keys, key],
    }));

  const toggleAddon = (key) =>
    setForm((f) => ({
      ...f,
      available_addons: f.available_addons.includes(key)
        ? f.available_addons.filter((k) => k !== key)
        : [...f.available_addons, key],
    }));

  const addonModules = modules.filter((m) => m.is_addon);

  const save = async () => {
    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      price_monthly: Number(form.price_monthly) || 0,
      price_yearly: Number(form.price_yearly) || 0,
      trial_days: Number(form.trial_days) || 0,
      sort_order: Number(form.sort_order) || 100,
      max_users: numOrNull(form.max_users),
      max_invoices_per_month: numOrNull(form.max_invoices_per_month),
      max_parties: numOrNull(form.max_parties),
      max_items: numOrNull(form.max_items),
    };
    try {
      if (editing) await api.patch(`/platform/plans/${editing.id}`, payload);
      else await api.post("/platform/plans", payload);
      toast.success(editing ? "Plan updated" : "Plan created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the ${p.name} plan? This cannot be undone.`)) return;
    try {
      await api.delete(`/platform/plans/${p.id}`);
      toast.success("Plan deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-plans-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Subscription Plans</h1>
          <p className="mt-1 text-sm text-slate-500">
            What each tier costs, which modules it unlocks, and its usage limits
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-plan-btn" className="gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700">
          <Plus size={16} /> New Plan
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {rows.map((p) => (
          <div
            key={p.id}
            className="flex flex-col rounded-lg border border-slate-200 bg-white p-5"
            data-testid={`plan-card-${p.code}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-heading text-lg font-bold">{p.name}</span>
                  {p.is_default && (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700">
                      <Star size={9} /> Default
                    </span>
                  )}
                  {!p.is_active && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="font-mono text-[11px] text-slate-500">{p.code}</div>
              </div>
              <button
                onClick={() => remove(p)}
                data-testid={`plan-delete-${p.code}`}
                className="text-slate-400 transition-colors duration-150 hover:text-rose-600"
                title="Delete plan"
              >
                <Trash2 size={15} />
              </button>
            </div>

            <div className="mt-3">
              <span className="font-heading text-2xl font-extrabold tabular-nums">{inr(p.price_monthly)}</span>
              <span className="text-xs text-slate-500"> /month</span>
              {p.price_yearly > 0 && (
                <div className="text-[11px] text-slate-500">{inr(p.price_yearly)} billed yearly</div>
              )}
            </div>

            <p className="mt-2 min-h-[32px] text-xs text-slate-600">{p.description}</p>

            <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-[11px] text-slate-600">
              <div className="flex justify-between"><span>Users</span><span>{limitLabel(p.max_users)}</span></div>
              <div className="flex justify-between"><span>Invoices / month</span><span>{limitLabel(p.max_invoices_per_month)}</span></div>
              <div className="flex justify-between"><span>Parties</span><span>{limitLabel(p.max_parties)}</span></div>
              <div className="flex justify-between"><span>Items</span><span>{limitLabel(p.max_items)}</span></div>
              {p.trial_days > 0 && (
                <div className="flex justify-between"><span>Trial</span><span>{p.trial_days} days</span></div>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-1 border-t border-slate-100 pt-3">
              {p.module_keys.map((k) => (
                <span key={k} className="rounded bg-violet-50 px-1.5 py-0.5 font-mono text-[10px] text-violet-700">
                  {k}
                </span>
              ))}
            </div>
            {!!(p.available_addons || []).length && (
              <div className="mt-2 border-t border-slate-100 pt-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">
                  Available add-ons (billed separately)
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.available_addons.map((k) => {
                    const m = modules.find((x) => x.key === k);
                    return (
                      <span key={k} className="rounded bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] text-amber-700">
                        {k}{m ? ` +${inr(m.addon_price_monthly)}` : ""}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-[11px] text-slate-500">
                {p.subscriber_count} subscriber{p.subscriber_count === 1 ? "" : "s"}
              </span>
              <button
                onClick={() => openEdit(p)}
                data-testid={`plan-edit-${p.code}`}
                className="text-xs font-medium text-violet-700 transition-colors duration-150 hover:text-violet-900"
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : "New plan"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Code</Label>
              <Input
                data-testid="plan-code-input"
                className="font-mono uppercase"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="GROWTH"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                data-testid="plan-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Description</Label>
              <Textarea
                data-testid="plan-description-input"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Price / month (₹)</Label>
              <Input
                data-testid="plan-monthly-input"
                className="text-right font-mono"
                value={form.price_monthly}
                onChange={(e) => setForm((f) => ({ ...f, price_monthly: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Price / year (₹)</Label>
              <Input
                data-testid="plan-yearly-input"
                className="text-right font-mono"
                value={form.price_yearly}
                onChange={(e) => setForm((f) => ({ ...f, price_yearly: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Trial days</Label>
              <Input
                data-testid="plan-trial-input"
                className="text-right font-mono"
                value={form.trial_days}
                onChange={(e) => setForm((f) => ({ ...f, trial_days: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sort order</Label>
              <Input
                data-testid="plan-sort-input"
                className="text-right font-mono"
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Limits — leave blank for unlimited</Label>
            <div className="grid gap-3 sm:grid-cols-4">
              {[
                ["max_users", "Users"],
                ["max_invoices_per_month", "Invoices/mo"],
                ["max_parties", "Parties"],
                ["max_items", "Items"],
              ].map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-[11px] text-slate-500">{label}</Label>
                  <Input
                    data-testid={`plan-${key}-input`}
                    className="text-right font-mono"
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder="∞"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Modules included</Label>
            <div className="grid gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-2" data-testid="plan-modules">
              {modules.map((m) => (
                <label
                  key={m.key}
                  className="flex cursor-pointer items-start gap-2 text-sm"
                  data-testid={`plan-module-${m.key}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-violet-600"
                    checked={form.module_keys.includes(m.key)}
                    onChange={() => toggleModule(m.key)}
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{m.name}</span>
                    {m.is_core && (
                      <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 text-[10px] uppercase text-slate-600">
                        core
                      </span>
                    )}
                    <span className="block text-[11px] text-slate-500">{m.description}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500">
              Core modules stay enabled for every tenant regardless of what you tick here.
            </p>
          </div>

          {!!addonModules.length && (
            <div className="space-y-2">
              <Label className="text-xs">Add-ons this plan may buy</Label>
              <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/40 p-3" data-testid="plan-addons">
                {addonModules.map((m) => (
                  <label key={m.key} className="flex cursor-pointer items-start gap-2 text-sm"
                    data-testid={`plan-addon-${m.key}`}>
                    <input type="checkbox" className="mt-1 accent-amber-600"
                      checked={form.available_addons.includes(m.key)}
                      onChange={() => toggleAddon(m.key)} />
                    <span className="min-w-0">
                      <span className="font-medium">{m.name}</span>
                      <span className="ml-1.5 font-mono text-[11px] text-amber-700">
                        +{inr(m.addon_price_monthly)}/mo
                      </span>
                      <span className="block text-[11px] text-slate-500">{m.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">
                Charged on top of the plan price, and only for organisations that actually buy it.
                Ticking a module above bundles it free instead.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.is_active}
                data-testid="plan-active-switch"
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.is_default}
                data-testid="plan-default-switch"
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_default: v }))}
              />
              Default for new signups
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="plan-save" className="bg-violet-600 hover:bg-violet-700">
              Save plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
